/**
 * Matematika orb: sebaran titik di permukaan bola, rotasi, proyeksi
 * perspektif, dan penskalaan kerapatan mengikuti ukuran render.
 *
 * Seluruh isi berkas ini fungsi murni tanpa DOM, jadi bisa diuji langsung
 * dengan `bun:test` tanpa kanvas maupun happy-dom.
 */

import type { OrbVariant } from "@/types/ai-assistant-orb";
import {
	ORB_MIN_POINTS,
	ORB_REFERENCE_SIZE_PX,
	ORB_SMALL_SIZE_PX,
} from "./orb.config";

export interface Point3 {
	x: number;
	y: number;
	z: number;
}

/** Sudut emas: 2π(1 − 1/φ). Menyebar titik tanpa pola bergaris. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * Sebaran `count` titik yang hampir merata di permukaan bola satuan
 * (Fibonacci sphere). Deterministik — tidak memakai `Math.random`, sehingga
 * orb terlihat sama di tiap muat ulang dan hasilnya bisa diuji.
 *
 * Dikembalikan sebagai `Float32Array` datar `[x,y,z, x,y,z, …]` supaya tidak
 * mengalokasi ribuan objek tiap frame.
 */
export function fibonacciSphere(count: number): Float32Array {
	const n = Math.max(1, Math.floor(count));
	const points = new Float32Array(n * 3);
	if (n === 1) {
		points[1] = 1;
		return points;
	}
	for (let i = 0; i < n; i++) {
		const y = 1 - (i / (n - 1)) * 2;
		const radius = Math.sqrt(Math.max(0, 1 - y * y));
		const theta = GOLDEN_ANGLE * i;
		points[i * 3] = Math.cos(theta) * radius;
		points[i * 3 + 1] = y;
		points[i * 3 + 2] = Math.sin(theta) * radius;
	}
	return points;
}

/** Rotasi terhadap sumbu Y (berputar) lalu sumbu X (kemiringan). */
export function rotatePoint(
	point: Point3,
	yawRad: number,
	pitchRad: number,
): Point3 {
	const cosYaw = Math.cos(yawRad);
	const sinYaw = Math.sin(yawRad);
	const x = point.x * cosYaw + point.z * sinYaw;
	const zYaw = point.z * cosYaw - point.x * sinYaw;
	const cosPitch = Math.cos(pitchRad);
	const sinPitch = Math.sin(pitchRad);
	return {
		x,
		y: point.y * cosPitch - zYaw * sinPitch,
		z: zYaw * cosPitch + point.y * sinPitch,
	};
}

/** Jarak kamera dari pusat bola satuan; makin kecil makin kuat perspektifnya. */
export const CAMERA_DISTANCE = 2.2;

export interface Projected {
	x: number;
	y: number;
	/** 0 = sisi terjauh, 1 = sisi terdekat. Dipakai untuk opasitas & ukuran titik. */
	depth: number;
}

/**
 * Proyeksi perspektif titik bola satuan ke koordinat kanvas.
 * `radius` = jari-jari bola dalam piksel kanvas.
 */
export function project(
	point: Point3,
	centerX: number,
	centerY: number,
	radius: number,
): Projected {
	const scale = (CAMERA_DISTANCE / (CAMERA_DISTANCE - point.z)) * radius;
	return {
		x: centerX + point.x * scale,
		y: centerY + point.y * scale,
		depth: (point.z + 1) / 2,
	};
}

/**
 * Kerapatan yang diminta admin berlaku pada ukuran acuan (56 px). Di ukuran
 * lain jumlah titik diskalakan dengan kuadrat rasio sisi — karena luas kanvas
 * tumbuh kuadratik, bukan linier. Tanpa ini, 1.600 titik di kanvas 22 px hanya
 * menghasilkan gumpalan warna.
 */
export function effectivePoints(density: number, cssSize: number): number {
	if (!Number.isFinite(density) || density <= 0) return ORB_MIN_POINTS;
	const ratio = Math.max(0, cssSize) / ORB_REFERENCE_SIZE_PX;
	const scaled = Math.round(density * ratio * ratio);
	return Math.max(ORB_MIN_POINTS, Math.min(Math.round(density), scaled));
}

/**
 * Di ukuran sangat kecil, titik dan garis tidak terbaca sebagai apa pun selain
 * noise — jadi varian rumit diturunkan ke cincin bercahaya, yang tetap
 * menyampaikan identitas visual yang sama.
 */
export function resolveVariantForSize(
	variant: OrbVariant,
	cssSize: number,
): OrbVariant {
	return cssSize < ORB_SMALL_SIZE_PX ? "glow-ring" : variant;
}
